<template>
  <div class="safe-area">
    <!-- Router View с анимациями -->
    <div class="pages-container">
      <router-view v-slot="{ Component, route }">
        <transition :name="transitionName" mode="out-in">
          <component :is="Component" :key="route.path" />
        </transition>
      </router-view>
    </div>

    <!-- Global Bottom Navigation — floating glass pill -->
    <transition name="nav-slide">
      <nav class="bottom-nav" v-if="showBottomNav">
        <div class="nav-pill" :style="{ transform: `translateX(calc(100% * ${navIndex}))` }"></div>
        <button
          class="nav-item"
          :class="{ active: currentRoute === '/' }"
          @click="goTo('')"
        >
          <span class="nav-icon-wrap">
            <span class="nav-icon" :class="{ anim: tapped === 'home' }">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M4 11l8-7 8 7v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round"/>
                <path class="door" d="M9.4 20v-6.2h5.2V20" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round"/>
              </svg>
            </span>
          </span>
          <span class="nav-label">Главная</span>
        </button>
        <button
          class="nav-item"
          :class="{ active: currentRoute === '/buyVPN' }"
          @click="goTo('buyVPN')"
        >
          <span class="nav-icon-wrap">
            <span class="nav-icon" :class="{ anim: tapped === 'buy' }">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <g class="cart">
                  <path d="M3 4h2l2.4 12.5a1 1 0 0 0 1 .8h8.7a1 1 0 0 0 1-.8L20.5 8H6" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="9" cy="20" r="1.4" fill="currentColor"/>
                  <circle cx="17" cy="20" r="1.4" fill="currentColor"/>
                </g>
              </svg>
            </span>
          </span>
          <span class="nav-label">Купить</span>
        </button>
        <button
          class="nav-item"
          :class="{ active: currentRoute === '/referal' }"
          @click="goTo('referal')"
        >
          <!-- ✅ Привлечение внимания: ярлык с выгодой + покачивание иконки.
               Исчезают после первого перехода в раздел -->
          <span class="nav-icon-wrap">
            <transition name="dot-pop">
              <span v-if="showReferralDot" class="nav-promo">+{{ REFERRAL_BONUS }} ₽</span>
            </transition>
            <span class="nav-icon wiggle" :class="{ tap: referralTapWiggle }">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="8" width="18" height="13" rx="2" stroke="currentColor" stroke-width="1.9"/><path d="M3 12h18M12 8v13" stroke="currentColor" stroke-width="1.9"/><path d="M12 8s-1-4-4-4-3 3 0 4M12 8s1-4 4-4 3 3 0 4" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg>
            </span>
          </span>
          <span class="nav-label">Реферал</span>
        </button>
        <button
          class="nav-item"
          :class="{ active: currentRoute === '/profile' }"
          @click="goTo('profile')"
        >
          <span class="nav-icon-wrap">
            <span class="nav-icon" :class="{ anim: tapped === 'profile' }">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle class="head" cx="12" cy="8" r="4" stroke="currentColor" stroke-width="1.9"/>
                <path class="body" d="M4 20c0-3.6 3.6-6 8-6s8 2.4 8 6" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/>
              </svg>
            </span>
          </span>
          <span class="nav-label">Профиль</span>
        </button>
      </nav>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch, computed, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { initTelegram, requestFullscreen, getSafeAreaInsets, isMobileDevice } from './utils/telegram';
import { haptic } from './utils/telegram';

const router = useRouter();
const route = useRoute();
const transitionName = ref('slide-left');

const safeArea = ref(getSafeAreaInsets({
  top: { value: isMobileDevice() ? 12 : 0, unit: 'vh' },
}));

// Определяем порядок страниц в навигации
const pageOrder: Record<string, number> = {
  '/': 0,
  '/buyVPN': 1,
  '/referal': 2,
  '/profile': 3,
  '/deposit': 10,
  '/tasks': 10,
  '/ChooseOSView': 10,
  '/payment-history': 10,
};

// Страницы где показывать bottom-nav (основные страницы)
const pagesWithNav = ['/', '/buyVPN', '/referal', '/profile'];

let previousPageIndex = 0;

const currentRoute = computed(() => route.path);

// Индекс активной вкладки для скользящей пилюли
const navIndexMap: Record<string, number> = {
  '/': 0,
  '/buyVPN': 1,
  '/referal': 2,
  '/profile': 3,
};
const navIndex = computed(() => navIndexMap[route.path] ?? 0);

// Показывать bottom-nav только на основных страницах
const showBottomNav = computed(() => {
  return pagesWithNav.includes(route.path);
});

/* ============================================
   ✅ ТОЧКА УВЕДОМЛЕНИЯ НА «РЕФЕРАЛ»
   Показывается, пока пользователь не открыл раздел.
   Флаг живёт в localStorage — точка не вернётся.
   ============================================ */
const REFERRAL_DOT_KEY = 'referralNavDotSeen';
const REFERRAL_BONUS = 250; // сумма в ярлыке над иконкой
const showReferralDot = ref(false);

const dismissReferralDot = () => {
  if (!showReferralDot.value) return;
  showReferralDot.value = false;
  try { localStorage.setItem(REFERRAL_DOT_KEY, 'true'); } catch (e) { /* noop */ }
};

// ✅ Одноразовое более заметное покачивание при нажатии (перезапускается каждый раз)
const referralTapWiggle = ref(false);
let referralTapTimer: ReturnType<typeof setTimeout> | null = null;

const playReferralWiggle = async () => {
  if (referralTapTimer) clearTimeout(referralTapTimer);
  referralTapWiggle.value = false;
  await nextTick();
  referralTapWiggle.value = true;
  referralTapTimer = setTimeout(() => { referralTapWiggle.value = false; }, 620);
};

watch(
  () => route.path,
  (newPath, oldPath) => {
    // ✅ Зашёл в раздел — точка пропадает
    if (newPath === '/referal') dismissReferralDot();

    const newIndex = pageOrder[newPath] ?? -1;
    const oldIndex = pageOrder[oldPath] ?? previousPageIndex;

    // Определяем направление анимации
    if (newIndex >= 10 && oldIndex < 10) {
      // Открытие модальной страницы
      transitionName.value = 'slide-up';
    } else if (oldIndex >= 10 && newIndex < 10) {
      // Закрытие модальной страницы
      transitionName.value = 'slide-down';
    } else if (newIndex > oldIndex) {
      // Переход вперед
      transitionName.value = 'slide-left';
    } else if (newIndex < oldIndex) {
      // Переход назад
      transitionName.value = 'slide-right';
    } else {
      // Неопределенный переход
      transitionName.value = 'fade';
    }

    previousPageIndex = newIndex !== -1 ? newIndex : oldIndex;
  },
  { immediate: true }
);

// ✅ Одноразовые анимации остальных иконок: дверь домика, едущая тележка, приподнятая голова
const tapped = ref<string | null>(null);
let tapTimer: ReturnType<typeof setTimeout> | null = null;

const playTap = async (name: string) => {
  if (tapTimer) clearTimeout(tapTimer);
  tapped.value = null;
  await nextTick();
  tapped.value = name;
  tapTimer = setTimeout(() => { tapped.value = null; }, 950);
};

const goTo = (route: string) => {
  haptic.impact();
  if (route === 'referal') {
    dismissReferralDot();
    playReferralWiggle(); // ✅ покачивание в ответ на нажатие
  } else {
    playTap(route === '' ? 'home' : route === 'buyVPN' ? 'buy' : 'profile');
  }
  router.push(`/${route}`);
};

// ✅ ИСПРАВЛЕНО: Асинхронная инициализация с обработкой ошибок
onMounted(async () => {
  // ✅ Точка уведомления: показываем, если раздел ещё не открывали
  try {
    showReferralDot.value = localStorage.getItem(REFERRAL_DOT_KEY) !== 'true' && route.path !== '/referal';
    if (route.path === '/referal') dismissReferralDot();
  } catch (e) {
    showReferralDot.value = true;
  }

  try {
    console.log('🚀 Starting Telegram initialization...');

    // Ждём загрузки и инициализации Telegram SDK
    await initTelegram();

    // После успешной инициализации запрашиваем fullscreen
    requestFullscreen();

    console.log('✅ App.vue: Telegram initialized successfully');
  } catch (error) {
    console.error('❌ App.vue: Failed to initialize Telegram:', error);

    // Можно показать fallback UI или уведомление пользователю
    // alert('Не удалось инициализировать Telegram WebApp. Пожалуйста, откройте приложение через Telegram.');
  }
});
onUnmounted(() => {
  if (referralTapTimer) clearTimeout(referralTapTimer);
  if (tapTimer) clearTimeout(tapTimer);
});
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');

/* ============================================
   ГЛОБАЛЬНАЯ ТЕМА (бело-зелёная, liquid glass)
   Design tokens
   ============================================ */
:root {
  --green: #12B76A;
  --green-dark: #0E9F5B;
  --green-deep: #0E7A46;
  --ink: #0F1512;
  --ink-2: #1c2723;
  --text-muted: #6B756F;
  --text-faint: #9AA39E;
  --hairline: #B2BAB5;
  --app-bg: #F5F7F6;
  --desk-bg: #E7ECE9;
  --surface: #FFFFFF;
  --surface-alt: #FBFCFB;
  --surface-alt2: #F7F9F8;
  --chip-bg: #EEF2F0;
  --chip-bg2: #F1F4F2;
  --border: #E9EDEB;
  --border-2: #E6EAE8;
  --border-3: #E4E9E6;
  --success-bg: #DCFCE7;
  --mint-bg: #F0FBF5;
  --mint-border: #C6F0D8;
  --track-bg: #EAF0ED;
  --danger: #E5484D;
  --danger-bg: #FDECEC;
}

* { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }

body {
  margin: 0;
  font-family: 'Manrope', system-ui, -apple-system, sans-serif;
  background: var(--desk-bg);
  color: var(--ink);
  min-height: 100vh;
  overflow: hidden;
  position: relative;
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--ink);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  height: 100vh;
  margin: 0;
  overflow: hidden;
  line-height: 1.5;
  position: relative;
  z-index: 1;
}

.safe-area {
  width: 100%;
  max-width: 480px;
  height: 100%;
  background: var(--app-bg);
  padding: v-bind('safeArea.top.value + safeArea.top.unit')
          v-bind('safeArea.right?.value + (safeArea.right?.unit ?? "px")')
          v-bind('safeArea.bottom?.value + (safeArea.bottom?.unit ?? "px")')
          v-bind('safeArea.left?.value + (safeArea.left?.unit ?? "px")');
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.pages-container {
  flex: 1;
  overflow: hidden;
  position: relative;
}

/* ============================================
   ANIMATED BOTTOM NAVIGATION
   ============================================ */

/* Анимация появления/исчезновения nav */
.nav-slide-enter-active {
  animation: navSlideUp 0.4s cubic-bezier(0.22, 1, 0.36, 1);
}

.nav-slide-leave-active {
  animation: navSlideDown 0.3s cubic-bezier(0.4, 0, 0.6, 1);
}

@keyframes navSlideUp {
  0% {
    opacity: 0;
    transform: translateX(-50%) translateY(100px) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1);
  }
}

@keyframes navSlideDown {
  0% {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateX(-50%) translateY(100px) scale(0.9);
  }
}

.bottom-nav {
  position: fixed;
  bottom: calc(14px + env(safe-area-inset-bottom));
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 28px);
  max-width: 452px;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(22px) saturate(180%);
  -webkit-backdrop-filter: blur(22px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.75);
  border-radius: 26px;
  display: flex;
  padding: 8px;
  z-index: 1000;
  box-shadow: 0 14px 34px rgba(16, 24, 20, 0.16),
              inset 0 1px 0 rgba(255, 255, 255, 0.9),
              inset 0 -1px 0 rgba(16, 24, 20, 0.03);
}

/* Скользящая пилюля активной вкладки */
.nav-pill {
  position: absolute;
  top: 8px;
  left: 8px;
  height: 60px;
  width: calc((100% - 16px) / 4);
  border-radius: 18px;
  background: rgba(18, 183, 106, 0.15);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5);
  z-index: 0;
  transition: transform 0.44s cubic-bezier(0.34, 1.42, 0.5, 1);
}

.nav-item {
  position: relative;
  z-index: 1;
  flex: 1;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 9px 4px;
  border-radius: 18px;
  color: #8A948E;
  transition: color 0.3s ease, transform 0.18s ease;
}

.nav-item:active {
  transform: scale(0.9);
}

.nav-item.active {
  color: var(--green);
}

.nav-label {
  font-size: 11px;
  font-weight: 700;
}

/* ✅ Анимации остальных иконок при нажатии */
.nav-icon svg .door,
.nav-icon svg .cart,
.nav-icon svg .head,
.nav-icon svg .body {
  transform-box: fill-box;
}

/* Домик: открывается дверь */
.nav-icon svg .door { transform-origin: left bottom; }
.nav-icon.anim svg .door { animation: doorOpen 0.9s cubic-bezier(0.34, 1.3, 0.5, 1); }

@keyframes doorOpen {
  0% { transform: scaleX(1); }
  38% { transform: scaleX(0); }
  62% { transform: scaleX(0); }
  100% { transform: scaleX(1); }
}

/* Тележка: целиком заезжает слева направо */
.nav-icon.anim svg .cart { animation: cartRoll 0.75s cubic-bezier(0.22, 1, 0.36, 1); }

@keyframes cartRoll {
  0% { transform: translateX(-13px); opacity: 0.25; }
  60% { transform: translateX(1.5px); opacity: 1; }
  100% { transform: translateX(0); opacity: 1; }
}

/* Профиль: голова подпрыгивает по параболе (баллистика + приземление) */
.nav-icon.anim svg .head { animation: headJump 0.62s linear; }
.nav-icon.anim svg .body { animation: bodySquash 0.62s linear; }

@keyframes headJump {
  0%   { transform: translateY(0); }
  8%   { transform: translateY(-1.75px); }
  18%  { transform: translateY(-2.85px); }
  30%  { transform: translateY(-3.2px); }
  42%  { transform: translateY(-2.85px); }
  52%  { transform: translateY(-1.75px); }
  64%  { transform: translateY(0); }
  72%  { transform: translateY(0.55px); }
  84%  { transform: translateY(-0.35px); }
  100% { transform: translateY(0); }
}

@keyframes bodySquash {
  0%   { transform: translateY(0) scaleY(1); }
  10%  { transform: translateY(0.5px) scaleY(0.97); }
  40%  { transform: translateY(0) scaleY(1); }
  70%  { transform: translateY(0.7px) scaleY(0.95); }
  86%  { transform: translateY(0) scaleY(1.01); }
  100% { transform: translateY(0) scaleY(1); }
}

/* ✅ Ярлык с выгодой и покачивание иконки */
.nav-icon-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-icon {
  display: flex;
  transform-origin: 50% 70%;
}

.nav-icon.wiggle {
  animation: navWiggle 3.4s ease-in-out infinite;
}

/* ✅ Покачивание по нажатию — перебивает фоновое */
.nav-icon.wiggle.tap {
  animation: navWiggleTap 0.6s cubic-bezier(0.34, 1.42, 0.5, 1);
}

@keyframes navWiggleTap {
  0% { transform: rotate(0) scale(1); }
  20% { transform: rotate(-15deg) scale(1.12); }
  45% { transform: rotate(13deg) scale(1.08); }
  70% { transform: rotate(-8deg) scale(1.04); }
  100% { transform: rotate(0) scale(1); }
}

@keyframes navWiggle {
  0%, 70%, 100% { transform: rotate(0); }
  76% { transform: rotate(-11deg); }
  84% { transform: rotate(9deg); }
  92% { transform: rotate(-5deg); }
}

.nav-promo {
  position: absolute;
  top: -13px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--green);
  color: #fff;
  font-size: 9.5px;
  font-weight: 800;
  line-height: 1;
  padding: 3px 7px;
  border-radius: 999px;
  white-space: nowrap;
  box-shadow: 0 4px 10px rgba(18, 183, 106, 0.35);
  pointer-events: none;
}

.dot-pop-enter-active {
  animation: dotPop 0.35s cubic-bezier(0.34, 1.42, 0.5, 1);
}

.dot-pop-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dot-pop-leave-to {
  opacity: 0;
  transform: translateX(-50%) scale(0.6);
}

@keyframes dotPop {
  0% { opacity: 0; transform: translateX(-50%) scale(0.4); }
  100% { opacity: 1; transform: translateX(-50%) scale(1); }
}

/* ============================================
   БЫСТРЫЕ АНИМАЦИИ СТРАНИЦ
   ============================================ */

.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-left-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
  filter: blur(10px);
}

.slide-left-enter-to {
  opacity: 1;
  transform: translateX(0) scale(1);
  filter: blur(0);
}

.slide-left-leave-from {
  opacity: 1;
  transform: translateX(0) scale(1);
  filter: blur(0);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-30%) scale(0.95);
  filter: blur(5px);
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-30%) scale(0.95);
  filter: blur(5px);
}

.slide-right-enter-to {
  opacity: 1;
  transform: translateX(0) scale(1);
  filter: blur(0);
}

.slide-right-leave-from {
  opacity: 1;
  transform: translateX(0) scale(1);
  filter: blur(0);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
  filter: blur(10px);
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from {
  opacity: 0;
  transform: translateY(100%) scale(0.95);
}

.slide-up-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.slide-up-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.slide-up-leave-to {
  opacity: 0;
  transform: translateY(-10%) scale(0.98);
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-10%) scale(0.98);
}

.slide-down-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.slide-down-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(100%) scale(0.95);
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-enter-from {
  opacity: 0;
  transform: scale(0.98);
  filter: blur(5px);
}

.fade-enter-to {
  opacity: 1;
  transform: scale(1);
  filter: blur(0);
}

.fade-leave-from {
  opacity: 1;
  transform: scale(1);
  filter: blur(0);
}

.fade-leave-to {
  opacity: 0;
  transform: scale(0.98);
  filter: blur(5px);
}

@media (prefers-reduced-motion: no-preference) {
  .slide-left-enter-active,
  .slide-left-leave-active,
  .slide-right-enter-active,
  .slide-right-leave-active,
  .slide-up-enter-active,
  .slide-up-leave-active,
  .slide-down-enter-active,
  .slide-down-leave-active {
    transition-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
  }
}

.slide-left-leave-active,
.slide-right-leave-active,
.slide-up-leave-active,
.slide-down-leave-active,
.fade-leave-active {
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
}

.slide-left-leave-active *,
.slide-right-leave-active *,
.slide-up-leave-active *,
.slide-down-leave-active * {
  user-select: none;
  -webkit-user-select: none;
}

::-webkit-scrollbar {
  width: 0;
}

::-webkit-scrollbar-thumb {
  background: rgba(18, 183, 106, 0.4);
  border-radius: 10px;
}
</style>
